/**
 * **Which queue row can be opened as a full session, and where that session lives.**
 *
 * The home draws a summary — a ring, a meta line, three chips — and nothing on the row led to the
 * rest of it: every exercise, every set, the client's remarks. `/klienci/$clientId?exec={id}` has
 * answered that since 12 August 2026 (owner: "kliknięcie w sesję ją otwiera"), and this module is
 * the one place that decides which row may say so.
 *
 * Pure: no React, no DOM, no query hook, no i18next. What leaves is two ids; the words and the
 * `Link` are the row's.
 *
 * ── It reads the ROW's own id, never the primary's ───────────────────────────────────────────
 * `model/taskAction` also holds a `workoutExecutionId` — the composer's — and deriving the link
 * from it would tie a secondary destination to whichever control the primary happens to be. The
 * two are independent decisions: a row whose primary degrades (no execution id, no client id) has
 * no session to link to for its OWN reasons.
 *
 * It read `detail.workoutExecutionId` until 16 September 2026, which is the FEEDBACK CARD's shape
 * and exists only for the four Feedback reasons. "Trening w połowie" is Triage, so its detail is
 * `plain` and the id it does carry was unreachable. `AttentionItem.workoutExecutionId` is the row's own
 * statement of which session it is about, and both reasons that have one read the same field.
 *
 * ── Both ids are required, and the client id is the one that is missing in practice ───────────
 * The address names a client and carries the session as a parameter; there is no route that opens a
 * session on its own. `subjectCustomerId` is `null` for exactly one reason — the pending invitation
 * (R10) — which is not a session reason, so in practice the pair is present or absent together. It
 * is still checked rather than assumed: `/klienci/0?exec=…` is a 404 dressed as a link.
 *
 * ── Which reasons reach this, and which two cannot ───────────────────────────────────────────
 * `sessionAwaitingReview` carries `WorkoutExecutionId` on the wire
 * (`SessionAwaitingReviewDetector`), and it is the case this exists for.
 *
 * `abandonedWorkout` carries it too — `AbandonedWorkoutDetector` sets `WorkoutExecutionId =
 * session.Id` — and joined it on 16 September 2026. `GET /workout-execution/{id}/display` has no
 * completion gate, so an unfinished session renders: its presenter's `finished` flag simply reads
 * false, and what the trainer opens is the half of the session that was done, which is the only
 * thing that would tell them why it stopped.
 *
 * `missedWorkout` cannot, and not for want of a row: its detector requires a `WorkoutExecution` to
 * classify the occurrence at all, and then puts only the session's date and name on the payload.
 * There is no id on the wire, so there is nothing to link to without a backend change.
 *
 * `newClip` carries `ClipWorkoutExecutionId`, and is deliberately not given a link — see
 * `ui/FeedbackClipRow`'s note and the report that came with it: the session panel cannot show the
 * film, the row's own primary IS the resolution (`exercise-clip-timeline` R9/R18), and an
 * unanchored clip has no session at all. That is why the reason is named here rather than inferred
 * from "the wire carried an id": the clip's id is a different field today, and a refusal that only
 * holds because of a field name would lapse silently the day the backend tidied it.
 */

import type { AttentionReason } from '~/entities/client';

import type { AttentionItem } from './attentionItems';

/**
 * The reasons whose row may open a session, named rather than inferred.
 *
 * They are exactly the detectors that put `WorkoutExecutionId` on the payload today, so the gate
 * changes nothing this build does — what it does is keep the clip's refusal deliberate. See the
 * header.
 */
const OPENS_A_SESSION: ReadonlySet<AttentionReason> = new Set([
  'sessionAwaitingReview',
  'abandonedWorkout',
  // The session the record was set in (R24), and the session that reported the newest low reading
  // (R22) — a reading a survey reported carries no execution and leads to the survey instead.
  'prAchieved',
  'lowSleep',
  'lowEnergy',
]);

/** A session the trainer can open in full, and the client whose address carries it. */
export interface SessionDestination {
  readonly clientId: number;
  readonly workoutExecutionId: number;
}

/**
 * Where this row's full session lives, or `null` where the row names no session the web can open.
 */
export function sessionDestination(item: AttentionItem): SessionDestination | null {
  if (!OPENS_A_SESSION.has(item.reason)) return null;

  const { workoutExecutionId, subjectCustomerId: clientId } = item;
  if (workoutExecutionId === null || clientId === null) return null;

  return { clientId, workoutExecutionId };
}
